// MA-C14 — side-by-side place-value comparison renderer.
//
// Draws base-ten blocks for TWO numbers (`a` and `b`) side-by-side with a
// neutral "?" separator — neither number is pre-marked, so the child must
// read the relative magnitudes (rod + cube counts) to answer the comparison.
// Registered under "place_value_comparison".
//
// Props: `a` (left number, 10..999), `b` (right number, 10..999). `total`
// and `op` are unused (the answer is not displayed).
import type { MathRendererProps } from "./rendererRegistry";

const FLAT   = "var(--pal-green, #5a9e6f)";              // hundreds flats
const ROD    = "var(--pal-orange, #d97742)";             // tens rods
const CUBE   = "var(--color-text-secondary, #7a6f60)";   // ones cubes
const STROKE = "var(--color-border-secondary, #cdbfae)";

const U   = 7;   // px per unit-cube edge
const GAP = 14;  // gap between block sub-groups within one number

type Rect = { x: number; y: number; w: number; h: number; fill: string };

/** Build the SVG rects for one number, with the left edge at x=0. */
function blocksFor(n: number): { rects: Rect[]; width: number } {
  const clamped  = Math.min(Math.max(0, Math.floor(Number.isFinite(n) ? n : 0)), 999);
  const hundreds = Math.floor(clamped / 100) % 10;
  const tens     = Math.floor(clamped / 10)  % 10;
  const ones     = clamped % 10;
  const flatSize = 10 * U;
  const rodH     = 10 * U;
  const rects: Rect[] = [];
  let x = 0;

  for (let i = 0; i < hundreds; i++) {
    rects.push({ x, y: 0, w: flatSize, h: flatSize, fill: FLAT });
    x += flatSize + 4;
  }
  if (hundreds) x += GAP - 4;
  for (let i = 0; i < tens; i++) {
    rects.push({ x, y: 0, w: U, h: rodH, fill: ROD });
    x += U + 3;
  }
  if (tens) x += GAP - 3;
  for (let i = 0; i < ones; i++) {
    rects.push({ x, y: (9 - i) * U, w: U, h: U, fill: CUBE });
  }
  if (ones) x += U;

  return { rects, width: Math.max(U, x) };
}

function PlaceValueComparisonRenderer({ a, b }: MathRendererProps) {
  const left  = Math.min(Math.max(10, Math.floor(Number.isFinite(a) ? a! : 10)), 999);
  const right = Math.min(Math.max(10, Math.floor(Number.isFinite(b) ? b! : 10)), 999);

  const SEP_W  = 28;          // "?" separator column width
  const PAD    = 4;           // inner left/right padding per number group
  const HEIGHT = 10 * U + 2; // 72px — enough for a tens rod (no hundreds needed for 2-digit)

  const lBlocks = blocksFor(left);
  const rBlocks = blocksFor(right);

  const lW       = lBlocks.width + PAD;
  const rOffsetX = lW + SEP_W;
  const totalW   = rOffsetX + rBlocks.width + PAD;

  return (
    <svg
      viewBox={`0 0 ${totalW} ${HEIGHT}`}
      width="100%"
      style={{ maxWidth: Math.min(420, totalW) }}
      role="img"
      aria-label={`Compare ${left} and ${right} using base-ten blocks`}
    >
      {/* Left number blocks */}
      {lBlocks.rects.map((r, i) => (
        <rect
          key={`l${i}`}
          x={r.x + 1}
          y={r.y + 1}
          width={r.w}
          height={r.h}
          rx="1"
          fill={r.fill}
          stroke={STROKE}
          strokeWidth="1"
        />
      ))}

      {/* Neutral "?" separator */}
      <text
        x={lW + SEP_W / 2}
        y={HEIGHT / 2 + 6}
        textAnchor="middle"
        fontSize="18"
        fontWeight="700"
        fill="var(--color-text-secondary, #7a6f60)"
      >?</text>

      {/* Right number blocks */}
      {rBlocks.rects.map((r, i) => (
        <rect
          key={`r${i}`}
          x={rOffsetX + r.x + 1}
          y={r.y + 1}
          width={r.w}
          height={r.h}
          rx="1"
          fill={r.fill}
          stroke={STROKE}
          strokeWidth="1"
        />
      ))}
    </svg>
  );
}

export default PlaceValueComparisonRenderer;
